"use client";

import React, { useEffect, useRef, useState } from "react";
import { Box, Skeleton, Stack, useMediaQuery } from "@mui/material";
import { ColletteCarousel } from "@collette-travel/collette-common-controls";
import { MediaCarousel } from "@/types/dto/homepage/homepage-types";
import { useTheme } from "@mui/material/styles";
import { SectionHeader } from "@/components/common/section-header";
import MediaCarouselCard from "./media-carousel-card";

interface MediaCarouselContainerProps {
  mediaCarouselData: MediaCarousel;
}

const MediaCarouselContainer = ({ mediaCarouselData }: MediaCarouselContainerProps) => {
  const theme = useTheme();
  const { basicInfo, mediaCards } = mediaCarouselData || {};

  // If less than 7 cards, duplicate the array to ensure smooth looping on bigger screens
  const slideCards = mediaCards;

  const skeletons = [
    { id: "media-skeleton-0" },
    { id: "media-skeleton-1" },
    { id: "media-skeleton-2" },
    { id: "media-skeleton-3" },
    { id: "media-skeleton-4" }
  ];

  const carouselRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // ...existing code...
  useEffect(() => {
    const carouselNode = carouselRef.current;
    if (!carouselNode) return;

    const updateActiveIndex = () => {
      // Find the track div
      // eslint-disable-next-line @typescript-eslint/quotes
      const track = carouselNode.querySelector('[data-testid="media-carousel-carousel-track"]');
      if (!track) return;

      // Get all direct children (slides)
      const slides = Array.from(track.children);
      slides.forEach((slide, idx) => {
        if (slide.classList.contains("is-active")) {
          setActiveIndex(idx);
        }
      });
    };

    updateActiveIndex();
    setIsLoading(false);

    const observer = new MutationObserver(updateActiveIndex);
    // Observe the track for class changes on its children
    // eslint-disable-next-line @typescript-eslint/quotes
    const track = carouselNode.querySelector('[data-testid="media-carousel-carousel-track"]');
    if (track) {
      observer.observe(track, { attributes: true, subtree: true, attributeFilter: ["class"] });
    }

    // eslint-disable-next-line consistent-return
    return () => observer.disconnect();
  }, [mediaCards]);
  // ...existing code...

  return (
    <Box
      className="fluid-box"
      sx={{
        backgroundColor: "background.cool",
        paddingY: "40px"
      }}
    >
      <Box
        className="full-width-container"
        component="section"
        sx={{
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          gap: "40px"
        }}
        data-track="visibility"
        data-parent-container-type="content"
        data-carousel-card-name="Media Carousel Card"
        data-carousel-position="8"
        data-module-name="Media Carousel Section"
        data-ranking-algorithm="manual"
        data-module-ranking-position="8"
      >
        <SectionHeader
          headerText={basicInfo?.headerText}
          copyText={basicInfo?.copyText?.replace(/<p>/g, "").replace(/<\/p>/g, "")}
          headerTestId="media-carousel-header"
          copyTestId="media-carosel-subheader"
          headerSx={{
            fontSize: { xs: "26px", sm: "28px", md: "32px" },
            fontWeight: "600",
            textAlign: "center",
            textTransform: "none!important"
          }}
          copySx={{ textAlign: "center", "& p": { margin: 0 } }}
          sx={{
            textAlign: "center",
            maxWidth: "650px",
            alignSelf: "center",
            paddingX: { xs: "24px", sm: "0px" }
          }}
        />
        <Box sx={{ width: "100%" }}>
          <div ref={carouselRef}>
            <Stack
              direction="row"
              gap="1rem"
              data-testid="media-carousel-skeleton"
              sx={{
                width: "144rem",
                marginTop: "2rem",
                justifySelf: "center",
                justifyContent: "center",
                overflow: "hidden",
                display: isLoading ? "flex" : "none"
              }}
            >
              {skeletons.map((skeleton, index) => (
                <Skeleton
                  key={skeleton.id}
                  variant="rounded"
                  sx={{
                    height: "46rem",
                    width: "26rem",
                    transform: index === 2 ? "scale(1)" : "scale(0.9)",
                    borderRadius: "2rem"
                  }}
                />
              ))}
            </Stack>
            <ColletteCarousel
              align="center"
              arrowPosition={isDesktop ? "top-right" : "bottom"}
              spacing={0}
              loop
              scaleNonActiveSlides={0.9}
              testId="media-carousel"
              variant="multi-card"
              slidesToScroll={1}
              arrowContainerSx={{
                right: isDesktop ? "70px" : 0,
                top: isDesktop ? "-30px" : undefined
              }}
              clickCardToSnap
              carouselRootSx={{
                display: isLoading ? "none" : "inline",
                opacity: isLoading ? 0 : 1,
                transition: "opacity ease-in 0.5s"
              }}
            >
              {slideCards?.map((card, index) => (
                <div key={card?.mediaCardId || index} style={{ cursor: "pointer" }}>
                  {(card?.media?.wistiaVideoId || card?.media?.imageUrl) && (
                    <MediaCarouselCard
                      card={card}
                      showOverlay={index === activeIndex}
                      index={activeIndex}
                    />
                  )}
                </div>
              ))}
            </ColletteCarousel>
          </div>
        </Box>
      </Box>
    </Box>
  );
};

export default MediaCarouselContainer;
